/*
 * The affiliate badge in the header: "Shopping with Star".
 *
 * A shopper who arrives through a GoAffPro link (`?ref=CODE`) carries a `ref`
 * cookie for seven days, and the app applies the affiliate's coupon at
 * checkout. Nothing on the page says so once the app's arrival popup is
 * dismissed. This fills the badge snippets/ap-affiliate-badge.liquid renders
 * hidden in both headers, so the relationship stays visible.
 *
 * Measured on the live store 2026-09-23, from `?ref=ESTRELLAFLORES`:
 *
 * - The app sets cookie `ref` (three copies across the host and the parent
 *   domain, one of them lowercased) and also mirrors it into localStorage.
 *   The cookie is the signal used here: localStorage `ref` never expires, so
 *   it outlives the attribution it records.
 * - On a FIRST arrival the cookie is not there yet when this runs: the app
 *   sets it after its own configuration fetch, well after DOMContentLoaded.
 *   Measured on the live theme 2026-09-23, a deployed badge stayed hidden on
 *   every `?ref=` visit from a fresh browser for exactly that reason. So the
 *   query parameter is read as well, and failing both, the cookie is polled
 *   for a few seconds before giving up.
 * - On every page load, including a cookie-only return visit, the app fetches
 *   the affiliate's public profile and caches it in sessionStorage under
 *   `gfpaff_<CODE>__` as JSON with `ref_code`, `name`, `first_name` and so
 *   on. That is the common path here and costs no request of our own.
 * - The endpoint it calls is public and answers a bare GET. It is only used
 *   below when the app's cache has not appeared, and asked for
 *   `ref_code,name,profile_photo` and nothing else. It will return `email` if
 *   asked; never ask.
 *
 * Every failure is "no badge", never an error: the storefront must not depend
 * on a third party's endpoint staying where it is.
 */
(function () {
  var ENDPOINT = 'https://api-server-3.goaffpro.com/v1/sdk/affiliate';
  var APP_CACHE_PREFIX = 'gfpaff_';
  var OWN_CACHE_PREFIX = 'ap-affiliate:';
  var POLL_MS = 250;
  var POLL_LIMIT_MS = 5000;

  function readRefCookie() {
    var cookie;
    try {
      cookie = document.cookie;
    } catch (cookieError) {
      return '';
    }
    var match = /(?:^|;\s*)ref=([^;]*)/.exec(cookie);
    if (!match) return '';
    var value;
    try {
      value = decodeURIComponent(match[1]).trim();
    } catch (decodeError) {
      return '';
    }
    return value ? value.toUpperCase() : '';
  }

  function readRefParam() {
    var value;
    try {
      value = new URLSearchParams(window.location.search).get('ref');
    } catch (parseError) {
      return '';
    }
    value = (value || '').trim();
    return value ? value.toUpperCase() : '';
  }

  function storageGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (storageError) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (storageError) {
      /* Private mode with storage denied: nothing to remember, which is harmless. */
    }
  }

  function profileOf(data) {
    return {
      name: typeof data.name === 'string' ? data.name.trim() : '',
      photo: typeof data.profile_photo === 'string' ? data.profile_photo.trim() : '',
    };
  }

  /* The app keys its cache by the code as it arrived, so the key's case can
     differ from the cookie's. Scan the prefix and match the code inside. */
  function appCachedProfile(code) {
    var keys = [];
    try {
      for (var i = 0; i < window.sessionStorage.length; i += 1) {
        var key = window.sessionStorage.key(i);
        if (key && key.indexOf(APP_CACHE_PREFIX) === 0) keys.push(key);
      }
    } catch (storageError) {
      return null;
    }
    for (var j = 0; j < keys.length; j += 1) {
      var data;
      try {
        data = JSON.parse(storageGet(keys[j]));
      } catch (parseError) {
        continue;
      }
      if (!data || typeof data.ref_code !== 'string') continue;
      if (data.ref_code.toUpperCase() !== code) continue;
      return profileOf(data);
    }
    return null;
  }

  function ownCachedProfile(code) {
    var raw = storageGet(OWN_CACHE_PREFIX + code);
    if (raw === null) return null;
    try {
      return profileOf(JSON.parse(raw));
    } catch (parseError) {
      return null;
    }
  }

  /* The photograph is optional twice over: an affiliate may not have one, and
     the one they have may fail to load. The initial is underneath either way,
     so a failed image is hidden rather than left as a broken glyph. */
  function showPhoto(badge, src) {
    var img = badge.querySelector('.ap-affiliate-badge__photo');
    if (!img || !src) return;
    img.addEventListener('error', function () {
      img.setAttribute('hidden', '');
    });
    img.src = src;
    img.removeAttribute('hidden');
  }

  /* The sticky header measured itself before the strip appeared; the hide
     offset it keeps in --header-height is short by the strip until it looks
     again. */
  function remeasureHeader() {
    var sticky = document.querySelector('sticky-header');
    if (sticky && typeof sticky.setHeaderHeight === 'function') sticky.setHeaderHeight();
  }

  function render(badges, profile) {
    if (!profile || !profile.name) return;
    var initial = profile.name.charAt(0).toUpperCase();
    for (var i = 0; i < badges.length; i += 1) {
      var badge = badges[i];
      var initialEl = badge.querySelector('.ap-affiliate-badge__initial');
      var nameEl = badge.querySelector('[data-affiliate-name]');
      var textEl = badge.querySelector('.ap-affiliate-badge__text');
      if (initialEl) initialEl.textContent = initial;
      if (nameEl) nameEl.textContent = profile.name;
      if (textEl) badge.setAttribute('title', textEl.textContent.replace(/\s+/g, ' ').trim());
      showPhoto(badge, profile.photo);
      badge.removeAttribute('hidden');
    }
    remeasureHeader();
  }

  function fetchName(badges, code, shop) {
    if (!window.fetch || !shop) return;
    var url =
      ENDPOINT +
      '?x-shop=' +
      encodeURIComponent(shop) +
      '&ref_code=' +
      encodeURIComponent(code) +
      '&fields=ref_code,name,profile_photo';
    window
      .fetch(url, { credentials: 'omit' })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        var profile = profileOf(data || {});
        storageSet(OWN_CACHE_PREFIX + code, JSON.stringify({ name: profile.name, profile_photo: profile.photo }));
        render(badges, profile);
      })
      .catch(function () {
        /* Endpoint moved, offline, or blocked: no badge. */
      });
  }

  function resolve(badges, code) {
    var cached = appCachedProfile(code);
    if (cached !== null) {
      render(badges, cached);
      return;
    }

    var own = ownCachedProfile(code);
    if (own !== null) {
      render(badges, own);
      return;
    }

    /* The app's own fetch is usually in flight at this point. Give it a
       moment before spending a request of our own. */
    var shop = badges[0].getAttribute('data-shop') || '';
    var waited = 0;
    var timer = window.setInterval(function () {
      waited += POLL_MS;
      var late = appCachedProfile(code);
      if (late !== null) {
        window.clearInterval(timer);
        render(badges, late);
        return;
      }
      if (waited >= POLL_LIMIT_MS) {
        window.clearInterval(timer);
        fetchName(badges, code, shop);
      }
    }, POLL_MS);
  }

  function init() {
    var badges = document.querySelectorAll('[data-affiliate-badge]');
    if (!badges.length) return;

    var code = readRefCookie() || readRefParam();
    if (code) {
      resolve(badges, code);
      return;
    }

    /* No cookie and no parameter: either an ordinary visit, which is the
       common case and ends here after a few cheap reads, or an arrival by one
       of the app's other identifiers whose cookie is still being written. */
    var waited = 0;
    var timer = window.setInterval(function () {
      waited += POLL_MS;
      var late = readRefCookie();
      if (late) {
        window.clearInterval(timer);
        resolve(badges, late);
        return;
      }
      if (waited >= POLL_LIMIT_MS) window.clearInterval(timer);
    }, POLL_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
